/**
 * Where an arche was opened from (ADR-037 D8) — the forge page's "Open
 * FiasWorks Studio", the Arche Builder's. A platform link adds
 * `?from=forge:<forgeId>` or `?from=builder:<sessionId>`. The arche page
 * remembers it for this tab, per arche (sessionStorage), so the arche's own
 * navigation does not lose it; `useArcheOpener` resolves it with the person's
 * own session — only their own forge or builder session — before the arche is
 * told its name. A link anyone can craft therefore names nothing it should not.
 */

export type OpenerKind = 'forge' | 'builder';

export interface OpenerRef {
  kind: OpenerKind;
  id: string;
}

export const OPENER_PARAM = 'from';

const ID_SHAPE: Record<OpenerKind, RegExp> = {
  forge: /^frg_[0-9a-f]{32}$/,
  builder: /^bsn_[a-f0-9]{32}$/,
};

/** `forge:frg_…` / `builder:bsn_…`, or null for anything else. */
export function parseOpener(value: string | null | undefined): OpenerRef | null {
  if (!value) return null;
  const at = value.indexOf(':');
  if (at < 0) return null;
  const kind = value.slice(0, at);
  const id = value.slice(at + 1);
  if (kind !== 'forge' && kind !== 'builder') return null;
  return ID_SHAPE[kind].test(id) ? { kind, id } : null;
}

/** A path with the opener added to its query. */
export function withOpener(path: string, ref: OpenerRef): string {
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}${OPENER_PARAM}=${encodeURIComponent(`${ref.kind}:${ref.id}`)}`;
}

const storageKey = (archeId: string) => `fias.opener.${archeId}`;

export function rememberOpener(archeId: string, ref: OpenerRef): void {
  try {
    sessionStorage.setItem(storageKey(archeId), `${ref.kind}:${ref.id}`);
  } catch {
    // Private mode / blocked storage: the opener lasts this page only.
  }
}

export function recallOpener(archeId: string): OpenerRef | null {
  try {
    return parseOpener(sessionStorage.getItem(storageKey(archeId)));
  } catch {
    return null;
  }
}

export function forgetOpener(archeId: string): void {
  try {
    sessionStorage.removeItem(storageKey(archeId));
  } catch {
    // Nothing to forget.
  }
}
